package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.element.Text
import kotlin.jvm.JvmStatic
import kotlin.math.roundToInt
import kotlin.random.Random

/**
 * 逐字乱码揭示（decipher）的纯算法工具。
 *
 * 挂载起在 `decipherDurationMs` 内，文字从乱码逐字定形为真文。每个字符位置有一个固定的随机揭示次序
 * `order`（rank），进度 `p` 推进时按 rank 从小到大逐位定形；未定形的位置每帧从 `charset` 随机取字，
 * 形成跳动乱码；空白字符（空格 / 换行）恒保持原样，不参与乱码。
 *
 * 状态（起始时刻 / 揭示次序）存放在 [Text] 元素上，本类只提供无副作用的纯函数，便于单元测试。
 */
object DecipherText {

    /**
     * 默认乱码字符集：仅取 Minecraft 默认字体可稳定渲染的 ASCII，避免 tofu。
     * 当 `text.decipherCharset()` 为 null 时使用。
     */
    const val DEFAULT_CHARSET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&!?@$/<>="

    /**
     * 计算给定文本长度的固定随机揭示次序（Fisher–Yates 洗牌得到的排列）。
     *
     * 同一元素用同一 `seed` 生成的 order 稳定不变，不同元素 seed 不同则次序不同。
     * 返回数组的第 `i` 项即位置 `i` 的 rank（揭示先后），rank 越小越早定形。
     *
     * @param length 文本字符数
     * @param seed   随机种子（建议取元素身份哈希，保证 per-element 稳定且互不相同）
     * @return 长度为 `length` 的排列数组；length<=0 时返回空数组
     */
    @JvmStatic
    fun buildOrder(length: Int, seed: Long): IntArray {
        if (length <= 0) {
            return IntArray(0)
        }
        val order = IntArray(length) { it }
        // 用固定种子洗牌，保证同一元素每帧拿到相同次序
        val random = Random(seed)
        for (i in length - 1 downTo 1) {
            val j = random.nextInt(i + 1)
            val tmp = order[i]
            order[i] = order[j]
            order[j] = tmp
        }
        return order
    }

    /**
     * 按进度把定形字数换算成揭示数量：`revealed = round(length * p)`，并夹紧到 [0, length]。
     *
     * @param length 文本字符数
     * @param p      进度（0~1，调用方负责夹紧）
     * @return 已定形字符数
     */
    @JvmStatic
    fun revealedCount(length: Int, p: Float): Int {
        if (length <= 0) {
            return 0
        }
        return (length * p).roundToInt().coerceIn(0, length)
    }

    /**
     * 生成当前帧的乱码态显示串。
     *
     * - 空白字符（空格 / 换行 / 制表）：恒保持原样，不乱码、不计入揭示判断。
     * - 已定形位（`order[i] < revealedCount`）：显示原字符。
     * - 未定形位：从 `charset` 随机取一个字符（每帧变化，制造跳动乱码）。
     *
     * @param original      原始文本
     * @param charset       乱码字符集（为空时回退 [DEFAULT_CHARSET]）
     * @param order         揭示次序（长度需与 `original` 一致，否则按越界即未定形处理）
     * @param revealedCount 已定形字符数
     * @param random        乱码取字随机源（注入便于测试）
     * @return 当前帧显示串；`original` 为 null / 空时原样返回
     */
    @JvmStatic
    fun decipherFrame(
        original: String?,
        charset: String?,
        order: IntArray?,
        revealedCount: Int,
        random: Random,
    ): String? {
        if (original.isNullOrEmpty()) {
            return original
        }
        val pool = if (charset.isNullOrEmpty()) DEFAULT_CHARSET else charset
        return buildString(original.length) {
            for (i in original.indices) {
                val c = original[i]
                // 空白恒保持原样：空格 / 换行 / 制表等
                if (c.isWhitespace()) {
                    append(c)
                    continue
                }
                val rank = if (order != null && i < order.size) order[i] else Int.MAX_VALUE
                // 已定形显示真字，否则取一个乱码字
                append(if (rank < revealedCount) c else pool[random.nextInt(pool.length)])
            }
        }
    }
}
